import { useState } from 'react';

import { PlusIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

import { personalizationSchema } from '@backend/orders/orders.personalization';

import { useCart } from '@frontend/storefront/components/cart/cart-context';
import {
  Product,
  ProductVariant,
} from '@frontend/storefront/lib/shopify/types';
import { useSearchParams } from '@frontend/storefront/navigation';

function SubmitButton({
  availableForSale,
  selectedVariantId,
}: {
  availableForSale: boolean;
  selectedVariantId: string | undefined;
}) {
  const buttonClasses =
    'relative flex w-full items-center justify-center rounded-[56px] bg-np-pink px-8 py-[18px] font-hand text-[22px] leading-none tracking-[-0.02em] text-np-bg transition';
  const disabledClasses = 'cursor-not-allowed opacity-60 hover:opacity-60';

  if (!availableForSale) {
    return (
      <button disabled className={clsx(buttonClasses, disabledClasses)}>
        Out of stock
      </button>
    );
  }

  if (!selectedVariantId) {
    return (
      <button
        aria-label="Please select an option"
        disabled
        className={clsx(buttonClasses, disabledClasses)}
      >
        <div className="absolute left-0 ml-4">
          <PlusIcon className="h-5" />
        </div>
        Add to cart
      </button>
    );
  }

  return (
    <button
      aria-label="Add to cart"
      className={clsx(buttonClasses, {
        'hover:-translate-y-px hover:brightness-110': true,
      })}
    >
      <div className="absolute left-0 ml-4">
        <PlusIcon className="h-5" />
      </div>
      Add to cart
    </button>
  );
}

export function AddToCart({ product }: { product: Product }) {
  const [error, setError] = useState<string>();
  const { variants, availableForSale } = product;
  const { addCartItem, isUpdating } = useCart();
  const searchParams = useSearchParams();

  const variant = variants.find((variant: ProductVariant) =>
    variant.selectedOptions.every(
      (option) => option.value === searchParams.get(option.name.toLowerCase()),
    ),
  );
  const defaultVariantId = variants.length === 1 ? variants[0]?.id : undefined;
  const selectedVariantId = variant?.id || defaultVariantId;
  const finalVariant = variants.find(
    (variant) => variant.id === selectedVariantId,
  );

  return (
    <form
      className="ph-no-capture ph-mask"
      onSubmit={(event) => {
        event.preventDefault();
        const fields = Object.fromEntries(new FormData(event.currentTarget));
        const result = personalizationSchema.safeParse({
          ...fields,
          age: Number(fields.age),
        });
        if (!result.success) {
          setError(result.error.issues[0]?.message);
          return;
        }
        setError(undefined);
        if (finalVariant) addCartItem(finalVariant, product, result.data);
      }}
    >
      <fieldset disabled={isUpdating} className="mb-6 grid gap-4">
        <legend className="mb-4 font-hand text-2xl">
          Tell us who this book is for
        </legend>
        {[
          ['name', 'Child’s name', 'text', '', 80],
          ['age', 'Child’s age', 'number', '', 2],
          ['language', 'Book language', 'text', 'English', 60],
        ].map(([name, label, type, defaultValue, maxLength]) => (
          <label key={name} className="grid gap-1 text-sm">
            {label}
            <input
              name={String(name)}
              type={String(type)}
              required
              defaultValue={defaultValue}
              min={type === 'number' ? 1 : undefined}
              max={type === 'number' ? 18 : undefined}
              maxLength={Number(maxLength)}
              className="rounded-xl border border-np-ink/25 bg-transparent px-3 py-2"
            />
          </label>
        ))}
        <label className="grid gap-1 text-sm">
          Reading level
          <select
            name="readingLevel"
            required
            defaultValue=""
            className="rounded-xl border border-np-ink/25 bg-transparent px-3 py-2"
          >
            <option value="" disabled>
              Choose how they enjoy books
            </option>
            <option value="read_aloud">Read aloud together</option>
            <option value="beginning">Beginning reader</option>
            <option value="confident">Confident reader</option>
          </select>
        </label>
        <label className="grid gap-1 text-sm">
          What would you like the book to be about?
          <textarea
            name="bookAbout"
            required
            maxLength={1000}
            rows={3}
            className="rounded-xl border border-np-ink/25 bg-transparent px-3 py-2"
          />
        </label>
        <p className="text-sm">
          After payment, we’ll email you a private interview link using your
          checkout email. You can complete it yourself or share it with the
          person who will.
        </p>
      </fieldset>
      {error && <p role="alert">{error}</p>}
      <fieldset disabled={isUpdating}>
        <SubmitButton
          availableForSale={availableForSale}
          selectedVariantId={selectedVariantId}
        />
      </fieldset>
    </form>
  );
}
